import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { type FormEvent, useEffect, useId, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { useApi } from '@/hooks/use-api'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

import type { VehicleRow } from './vehicle-types'

/**
 * Vehicle create / edit dialog.
 *
 * The vehicle form has 13 fields covering identity, mileage,
 * financial info, and the active flag. We group them in 4
 * visually-separated sections so the admin doesn't read it as a
 * 13-row column of inputs:
 *
 *   1. **Identification** — registration, type, brand, model, VIN.
 *   2. **Kilométrage** — initial / current.
 *   3. **Financier** — fuel price, hourly rate, purchase value,
 *      depreciation, residual, commission date.
 *   4. **Statut** — active toggle.
 *
 * Money fields go to the API as `number` (the schema accepts
 * `z.number().nullish()` and the service converts to the
 * `numeric(10,2)` string). Empty strings → `null` so we don't
 * ship `0` for an unset field.
 */

interface VehicleDialogProps {
  vehicle: VehicleRow | null | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface FormState {
  registration: string
  type: string
  brand: string
  model: string
  vin: string
  initialMileage: string
  currentMileage: string
  fuelPricePerLiter: string
  rentalRate: string
  purchaseValue: string
  depreciationMonths: string
  residualValue: string
  commissionDate: string
  active: boolean
}

interface FormErrors {
  registration?: string
}

const EMPTY_FORM: FormState = {
  registration: '',
  type: '',
  brand: '',
  model: '',
  vin: '',
  initialMileage: '0',
  currentMileage: '',
  fuelPricePerLiter: '',
  rentalRate: '',
  purchaseValue: '',
  depreciationMonths: '',
  residualValue: '',
  commissionDate: '',
  active: true,
}

export function VehicleDialog({ vehicle, open, onOpenChange }: VehicleDialogProps) {
  const api = useApi()
  const queryClient = useQueryClient()

  const titleId = useId()
  const isEdit = vehicle !== undefined && vehicle !== null

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (vehicle !== undefined && vehicle !== null) {
      setForm({
        registration: vehicle.registration,
        type: vehicle.type ?? '',
        brand: vehicle.brand ?? '',
        model: vehicle.model ?? '',
        vin: vehicle.vin ?? '',
        initialMileage: String(vehicle.initialMileage),
        currentMileage:
          vehicle.currentMileage === null ? '' : String(vehicle.currentMileage),
        fuelPricePerLiter: vehicle.fuelPricePerLiter ?? '',
        rentalRate: vehicle.rentalRate ?? '',
        purchaseValue: vehicle.purchaseValue ?? '',
        depreciationMonths:
          vehicle.depreciationMonths === null
            ? ''
            : String(vehicle.depreciationMonths),
        residualValue: vehicle.residualValue ?? '',
        commissionDate: vehicle.commissionDate ?? '',
        active: vehicle.active,
      })
    } else {
      setForm(EMPTY_FORM)
    }
    setErrors({})
    setSubmitError(null)
  }, [open, vehicle])

  const saveMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const payload = buildPayload(form)
      if (isEdit) {
        await api.patch<unknown>(`/api/vehicles/${vehicle.id}`, payload)
      } else {
        await api.post<unknown>('/api/vehicles', payload)
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      onOpenChange(false)
    },
    onError: (err) => {
      setSubmitError(
        err instanceof ApiError
          ? err.message
          : "Impossible d'enregistrer le véhicule.",
      )
    },
  })

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    const validation = validate(form)
    setErrors(validation)
    if (Object.keys(validation).length > 0) return
    setSubmitError(null)
    saveMutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" aria-labelledby={titleId}>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle id={titleId}>
              {isEdit ? 'Modifier le véhicule' : 'Nouveau véhicule'}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? 'Mettez à jour les informations du véhicule.'
                : 'Ajoutez un véhicule à votre parc.'}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="flex flex-col gap-5">
            {/* Section 1 — Identification */}
            <Section title="Identification">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Immatriculation *" error={errors.registration}>
                  <Input
                    type="text"
                    required
                    value={form.registration}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        registration: e.target.value.toUpperCase(),
                      })
                    }
                    invalid={errors.registration !== undefined}
                    placeholder="AB-123-CD"
                  />
                </Field>
                <Field label="Type">
                  <Input
                    type="text"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    placeholder="benne, toupie, grue…"
                    list="vehicle-types"
                  />
                  {/* Suggestion list — accepted values aren't a
                      hard enum so we let the admin type new
                      types. The existing values appear via
                      datalist (lower friction than a dropdown). */}
                  <datalist id="vehicle-types">
                    <option value="benne" />
                    <option value="toupie" />
                    <option value="grue" />
                    <option value="camion" />
                    <option value="tractopelle" />
                    <option value="utilitaire" />
                  </datalist>
                </Field>
                <Field label="Marque">
                  <Input
                    type="text"
                    value={form.brand}
                    onChange={(e) => setForm({ ...form, brand: e.target.value })}
                    placeholder="Renault, Volvo, Iveco…"
                  />
                </Field>
                <Field label="Modèle">
                  <Input
                    type="text"
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                    placeholder="Kerax, FH16, Trakker…"
                  />
                </Field>
                <Field label="VIN" wide>
                  <Input
                    type="text"
                    value={form.vin}
                    onChange={(e) =>
                      setForm({ ...form, vin: e.target.value.toUpperCase() })
                    }
                    placeholder="Numéro de série constructeur"
                  />
                </Field>
              </div>
            </Section>

            {/* Section 2 — Kilométrage */}
            <Section title="Kilométrage">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Kilométrage initial">
                  <Input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={form.initialMileage}
                    onChange={(e) =>
                      setForm({ ...form, initialMileage: e.target.value })
                    }
                  />
                </Field>
                <Field label="Kilométrage actuel">
                  <Input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={form.currentMileage}
                    onChange={(e) =>
                      setForm({ ...form, currentMileage: e.target.value })
                    }
                  />
                </Field>
              </div>
            </Section>

            {/* Section 3 — Financier */}
            <Section title="Financier">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Prix carburant (€/L)">
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={form.fuelPricePerLiter}
                    onChange={(e) =>
                      setForm({ ...form, fuelPricePerLiter: e.target.value })
                    }
                  />
                </Field>
                <Field label="Tarif location (€/h)">
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={form.rentalRate}
                    onChange={(e) =>
                      setForm({ ...form, rentalRate: e.target.value })
                    }
                  />
                </Field>
                <Field label="Valeur d'achat (€)">
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={form.purchaseValue}
                    onChange={(e) =>
                      setForm({ ...form, purchaseValue: e.target.value })
                    }
                  />
                </Field>
                <Field label="Amortissement (mois)">
                  <Input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={form.depreciationMonths}
                    onChange={(e) =>
                      setForm({ ...form, depreciationMonths: e.target.value })
                    }
                  />
                </Field>
                <Field label="Valeur résiduelle (€)">
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    value={form.residualValue}
                    onChange={(e) =>
                      setForm({ ...form, residualValue: e.target.value })
                    }
                  />
                </Field>
                <Field label="Date mise en service">
                  <Input
                    type="date"
                    value={form.commissionDate}
                    onChange={(e) =>
                      setForm({ ...form, commissionDate: e.target.value })
                    }
                  />
                </Field>
              </div>
            </Section>

            {/* Section 4 — Statut */}
            <Section title="Statut">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) =>
                    setForm({ ...form, active: e.target.checked })
                  }
                  className="size-4 rounded border-2 border-surface-border text-brand"
                />
                <span className="text-sm">
                  Véhicule actif (apparaît dans les sélecteurs des plannings et
                  missions)
                </span>
              </label>
            </Section>

            {submitError !== null ? (
              <p
                role="alert"
                className="text-sm text-danger text-center font-medium"
              >
                {submitError}
              </p>
            ) : null}
          </DialogBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Annuler
              </Button>
            </DialogClose>
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? (
                <Spinner label="Enregistrement" />
              ) : (
                <>
                  <Save size={16} />
                  {isEdit ? 'Enregistrer' : 'Créer'}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* -------------------------------------------------------------------------- */
/*  Section + Field helpers                                                    */
/* -------------------------------------------------------------------------- */

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <fieldset className="border-t border-surface-border pt-4 first:border-t-0 first:pt-0">
      <legend className="text-xs font-semibold uppercase tracking-wide text-text-muted mb-2">
        {title}
      </legend>
      {children}
    </fieldset>
  )
}

function Field({
  label,
  error,
  wide = false,
  children,
}: {
  label: string
  // `string | undefined` rather than `string` so callers can
  // pass an optional that resolves to `undefined` without TS
  // erroring under `exactOptionalPropertyTypes`.
  error?: string | undefined
  wide?: boolean
  children: React.ReactNode
}) {
  return (
    <div
      className={cn('flex flex-col gap-1.5', wide ? 'sm:col-span-2' : '')}
    >
      <Label>{label}</Label>
      {children}
      {error !== undefined ? (
        <p className="text-xs text-danger">{error}</p>
      ) : null}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Validation + payload                                                       */
/* -------------------------------------------------------------------------- */

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {}
  if (form.registration.trim() === '') {
    errors.registration = "L'immatriculation est obligatoire."
  }
  return errors
}

interface VehiclePayload {
  registration: string
  active: boolean
  initialMileage: number
  type?: string | null
  brand?: string | null
  model?: string | null
  vin?: string | null
  currentMileage?: number | null
  fuelPricePerLiter?: number | null
  rentalRate?: number | null
  purchaseValue?: number | null
  depreciationMonths?: number | null
  residualValue?: number | null
  commissionDate?: string | null
}

function buildPayload(form: FormState): VehiclePayload {
  return {
    registration: form.registration.trim(),
    active: form.active,
    initialMileage: numberOrZero(form.initialMileage),
    type: nullIfBlank(form.type),
    brand: nullIfBlank(form.brand),
    model: nullIfBlank(form.model),
    vin: nullIfBlank(form.vin),
    currentMileage: nullableInt(form.currentMileage),
    fuelPricePerLiter: nullableDecimal(form.fuelPricePerLiter),
    rentalRate: nullableDecimal(form.rentalRate),
    purchaseValue: nullableDecimal(form.purchaseValue),
    depreciationMonths: nullableInt(form.depreciationMonths),
    residualValue: nullableDecimal(form.residualValue),
    commissionDate: nullIfBlank(form.commissionDate),
  }
}

function nullIfBlank(s: string): string | null {
  const trimmed = s.trim()
  return trimmed === '' ? null : trimmed
}

function nullableInt(s: string): number | null {
  const trimmed = s.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (!Number.isFinite(n)) return null
  return Math.trunc(n)
}

function nullableDecimal(s: string): number | null {
  const trimmed = s.trim().replace(',', '.')
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 100) / 100
}

function numberOrZero(s: string): number {
  const n = nullableInt(s)
  return n ?? 0
}
